import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ProjectInfo from "./ProjectInfo";
import "./Sidebar.css";
import DocManagementLayout from "./DocManagementLayout";
import Icon12 from "../img/7178d169a7be088cdc1b5e359f57869b33e4aa6356998dd541a860989097bb95.svg";
import projectIconWhite from "../img/540c6ef86ee7b47ca5a7ffc2f89e05a3b1bea144157f7be115142ac4f72d107a.svg";
import projectIconBlack from "../img/ca5a7ffc2f89e05a3b1bea144157f7be115142ac4f72d107a.svg";
import manageContactsBlack from "../img/4fc2b890fdab97816c49ad8e37fc297bda941f891813707e7dba20bf5456a9ec.svg";
import manageContactsWhite from "../img/manageContactsWhite.svg";
import LogoImage from "../img/a6fbc02e1270717ab963b6d5928edcec5bb01a1174b13c49daba957378db2aa2.png";
import ExternalLinksSection from "./ExternalLinksSection";
import { RecentDashboardsSidebarSection } from "./RecentDashboards";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { useStandalonePwa } from "../hooks/useStandalonePwa";
import { useCompactNav } from "../utils/compactNav";
import ReportPickerModal, { applyReportSelection } from "./ReportPickerModal";

function formatPinMoney(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function SidebarProjectPin({ projectData }) {
  const { authFetch, isReady } = useAuthFetch();
  const [financials, setFinancials] = useState({
    originalContractAmount: 0,
    approvedContractCOs: 0,
    currentContractAmount: 0,
    potentialCOsAmount: 0,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectData?.projectId || !projectData?.division || !isReady) return;
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/finicial-budget?projectID=${projectData.projectId}&divisionName=${projectData.division}`
        );
        const data = await response.json();
        const findValue = (label) => {
          const item = data.budgetProgressData?.find((row) => row.label === label);
          return item ? item.value : 0;
        };
        if (cancelled) return;
        setFinancials({
          originalContractAmount: findValue("Original Contract Amount"),
          approvedContractCOs: findValue("Approved Contract COs"),
          currentContractAmount: findValue("Current Contract Amount"),
          potentialCOsAmount: findValue("Potential COs Amount"),
        });
      } catch (error) {
        console.error("Error fetching financial data:", error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectData?.projectId, projectData?.division, authFetch, isReady]);

  const facts = [
    ["Branch", projectData.company || "–"],
    ["Scope", projectData.scope || "–"],
    ["Delivery Method", projectData.deliveryMethod || "–"],
    ["Opportunity No", projectData.opportunityNo || "–"],
    ["Org. Contract Amt", formatPinMoney(financials.originalContractAmount)],
    ["Approved CO Amt", formatPinMoney(financials.approvedContractCOs)],
    ["Current Contract Amt", formatPinMoney(financials.currentContractAmount)],
    ["Potential CO Amt", formatPinMoney(financials.potentialCOsAmount)],
  ];

  return (
    <div className="sidebar-project-pin" title={projectData.projectName}>
      <span className="sidebar-project-pin__kicker">Now viewing</span>
      <span className="sidebar-project-pin__name">{projectData.projectName}</span>
      {projectData.projectNo ? <span className="sidebar-project-pin__no">{projectData.projectNo}</span> : null}
      <dl className="sidebar-project-pin__facts">
        {facts.map(([label, value]) => (
          <div className="sidebar-project-pin__fact" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Sidebar({ isCollapsed, projectData, isMobileMenuOpen, onMobileMenuClose, onMenuToggle, showPinnedProjectName }) {
  const location = useLocation();
  const isStandalonePwa = useStandalonePwa();
  const isManageContactsPage = location.pathname === '/contacts/manage';

  const [isProjectInfoExpanded, setIsProjectInfoExpanded] = useState(false);
  const [isDocManagementExpanded, setIsDocManagementExpanded] = useState(false);
  const [isLinksExpanded, setIsLinksExpanded] = useState(false);
  const [isReportPickerOpen, setIsReportPickerOpen] = useState(false);
  const rootNavigate = useNavigate();
  const isMobile = useCompactNav();

  const openSelectedReport = (selection) => {
    setIsReportPickerOpen(false);
    const params = applyReportSelection(new URLSearchParams(location.search), selection);
    rootNavigate({ pathname: location.pathname, search: params.toString() });
    if (isMobile) onMobileMenuClose?.();
  };

  // Get selected department from localStorage or use default
  const [selectedDepartment, setSelectedDepartment] = useState(() =>
    localStorage.getItem('selectedDepartment') || 'Engineered Fill'
  );

  const [isProjectSelected, setIsProjectSelected] = useState(() =>
    localStorage.getItem('isProjectSelected') === 'true'
  );

  useEffect(() => {
    const handleDepartmentChange = (event) => {
      setSelectedDepartment(event.detail);
      setIsProjectSelected(true);
    };

    const handleProjectDataCleared = () => {
      setIsProjectSelected(false);
    };

    const handleProjectDetailsChanged = () => {
      setIsProjectSelected(true);
      const department = localStorage.getItem('selectedDepartment');
      if (department) {
        setSelectedDepartment(department);
      }
    };

    window.addEventListener('departmentChanged', handleDepartmentChange);
    window.addEventListener('projectDataCleared', handleProjectDataCleared);
    window.addEventListener('projectDetailsChanged', handleProjectDetailsChanged);
    return () => {
      window.removeEventListener('departmentChanged', handleDepartmentChange);
      window.removeEventListener('projectDataCleared', handleProjectDataCleared);
      window.removeEventListener('projectDetailsChanged', handleProjectDetailsChanged);
    };
  }, []);

  useEffect(() => {
    setIsProjectSelected(localStorage.getItem('isProjectSelected') === 'true');
    const department = localStorage.getItem('selectedDepartment');
    if (department) {
      setSelectedDepartment(department);
    }
  }, [location.pathname]);

  // Handle menu item click - close mobile menu
  const handleMenuClick = () => {
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleMobileMenuToggle = () => {
    if (onMenuToggle) {
      onMenuToggle();
      return;
    }
    if (onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const isOnProjectList = location.pathname === '/';
  const isGlobalNavPage = ['/', '/contacts', '/contacts/manage', '/accounts', '/inventory', '/equipment', '/archive'].includes(
    location.pathname
  );
  const showGlobalMobileNav = isMobile;
  const showProjectSidebar = !isMobile || !isGlobalNavPage;

  const dashboardLabel =
    isProjectSelected && !isOnProjectList
      ? `Dashboard - ${selectedDepartment}`
      : 'Dashboard';

  const renderNavLink = (item) => (
    <div key={item.id} className={`menu-item-wrapper ${item.active ? 'active' : ''}`}>
      <div className={`indicator ${item.active ? 'active' : ''}`} />
      <Link to={item.path} className="menu-item" onClick={handleMenuClick}>
        {item.icon}
        <span className="menu-text">{item.label}</span>
      </Link>
    </div>
  );

  const menuItems = [
    {
      id: 'project-list',
      label: 'Projects',
      icon: location.pathname === '/' ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4C4 3.44772 4.44772 3 5 3H15C15.5523 3 16 3.44772 16 4V16C16 16.5523 15.5523 17 15 17H5C4.44772 17 4 16.5523 4 16V4Z"
            stroke={location.pathname === '/' ? "#DC2626" : "#374151"}
            strokeWidth="2"
          />
          <circle cx="8" cy="8" r="1"
            fill={location.pathname === '/' ? "#DC2626" : "#374151"}
          />
          <path d="M10 8H13"
            stroke={location.pathname === '/' ? "#DC2626" : "#374151"}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="8" cy="12" r="1"
            fill={location.pathname === '/' ? "#DC2626" : "#374151"}
          />
          <path d="M10 12H13"
            stroke={location.pathname === '/' ? "#DC2626" : "#374151"}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4C4 3.44772 4.44772 3 5 3H15C15.5523 3 16 3.44772 16 4V16C16 16.5523 15.5523 17 15 17H5C4.44772 17 4 16.5523 4 16V4Z"
            stroke="#374151"
            strokeWidth="2"
          />
          <circle cx="8" cy="8" r="1"
            fill="#374151"
          />
          <path d="M10 8H13"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="8" cy="12" r="1"
            fill="#374151"
          />
          <path d="M10 12H13"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      path: '/',
      active: location.pathname === '/'
    },
    {
      id: 'dashboard',
      label: dashboardLabel,
      icon: location.pathname === '/dashboard' ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
            stroke="#DC2626"
            strokeWidth="2"
          />
          <path d="M10 10L14 6"
            stroke="#DC2626"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path d="M9.5 10C9.5 10.2761 9.72386 10.5 10 10.5C10.2761 10.5 10.5 10.2761 10.5 10C10.5 9.72386 10.2761 9.5 10 9.5C9.72386 9.5 9.5 9.72386 9.5 10Z"
            stroke="#DC2626"
            strokeWidth="2"
          />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
            stroke="#374151"
            strokeWidth="2"
          />
          <path d="M10 10L14 6"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path d="M9.5 10C9.5 10.2761 9.72386 10.5 10 10.5C10.2761 10.5 10.5 10.2761 10.5 10C10.5 9.72386 10.2761 9.5 10 9.5C9.72386 9.5 9.5 9.72386 9.5 10Z"
            stroke="#374151"
            strokeWidth="2"
          />
        </svg>
      ),
      path: '/dashboard',
      active: (location.pathname === '/dashboard' || location.pathname.startsWith('/project/')) && !['daily-report', 'report'].includes(new URLSearchParams(location.search).get('view'))
    },
    {
      id: 'manage-contacts',
      label: 'Contacts',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 17V15C16 13.9391 15.5786 12.9217 14.8284 12.1716C14.0783 11.4214 13.0609 11 12 11H8C6.93913 11 5.92172 11.4214 5.17157 12.1716C4.42143 12.9217 4 13.9391 4 15V17"
            stroke={location.pathname === '/contacts/manage' ? "#DC2626" : "#374151"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M10 7C11.6569 7 13 5.65685 13 4C13 2.34315 11.6569 1 10 1C8.34315 1 7 2.34315 7 4C7 5.65685 8.34315 7 10 7Z"
            stroke={location.pathname === '/contacts/manage' ? "#DC2626" : "#374151"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      path: '/contacts/manage',
      active: location.pathname === '/contacts/manage'
    },
    {
      id: 'account-management',
      label: 'Accounts',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8" stroke={location.pathname === '/accounts' ? "#DC2626" : "#374151"} strokeWidth="2" />
          <path d="M10 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke={location.pathname === '/accounts' ? "#DC2626" : "#374151"} strokeWidth="2" />
          <path d="M4 16c0-2.209 2.686-4 6-4s6 1.791 6 4" stroke={location.pathname === '/accounts' ? "#DC2626" : "#374151"} strokeWidth="2" />
        </svg>
      ),
      path: '/accounts',
      active: location.pathname === '/accounts'
    },
    {
      id: 'inventory-management',
      label: 'Inventory',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6l7-3 7 3v8l-7 3-7-3V6z" stroke={location.pathname === '/inventory' ? "#DC2626" : "#374151"} strokeWidth="2" strokeLinejoin="round" />
          <path d="M3 6l7 3 7-3M10 9v9" stroke={location.pathname === '/inventory' ? "#DC2626" : "#374151"} strokeWidth="2" />
        </svg>
      ),
      path: '/inventory',
      active: location.pathname === '/inventory'
    },
    {
      id: 'equipment-management',
      label: 'Equipment',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 13h10.5a2.5 2.5 0 0 0 2.45-2H18V8.5h-2.2l-1.3-2.2A2 2 0 0 0 12.8 5H8.5L7 7H3v6z" stroke={location.pathname === '/equipment' ? "#DC2626" : "#374151"} strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="6.5" cy="14.5" r="1.6" stroke={location.pathname === '/equipment' ? "#DC2626" : "#374151"} strokeWidth="1.6" />
          <circle cx="13.5" cy="14.5" r="1.6" stroke={location.pathname === '/equipment' ? "#DC2626" : "#374151"} strokeWidth="1.6" />
        </svg>
      ),
      path: '/equipment',
      active: location.pathname === '/equipment'
    },
    {
      id: 'archive',
      label: 'Archive',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="14" height="4" rx="1" stroke={location.pathname === '/archive' ? "#DC2626" : "#374151"} strokeWidth="2" />
          <rect x="4" y="7" width="12" height="10" rx="1" stroke={location.pathname === '/archive' ? "#DC2626" : "#374151"} strokeWidth="2" />
          <path d="M7 11H13" stroke={location.pathname === '/archive' ? "#DC2626" : "#374151"} strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
      path: '/archive',
      active: location.pathname === '/archive'
    }
  ];

  const projectsItem = menuItems.find(item => item.id === 'project-list');
  const dashboardItem = menuItems.find(item => item.id === 'dashboard');
  const contactsItem = menuItems.find(item => item.id === 'manage-contacts');
  const accountsItem = menuItems.find(item => item.id === 'account-management');
  const inventoryItem = menuItems.find(item => item.id === 'inventory-management');
  const equipmentItem = menuItems.find(item => item.id === 'equipment-management');
  const archiveItem = menuItems.find(item => item.id === 'archive');

  const globalNavItems = [projectsItem, contactsItem, accountsItem, inventoryItem, equipmentItem, archiveItem].filter(Boolean);

  const links = [
    "Production Report",
    "Reports",
    "Acumatica",
    "Salesforce"
  ];

  const ProjectInfoSection = () => {
    const handleProjectInfoClick = () => {
      setIsProjectInfoExpanded(!isProjectInfoExpanded);
    };

    return (
      <>
        <div className={`menu-item-wrapper ${isProjectInfoExpanded ? 'active' : ''}`}>
          <div className={`indicator ${isProjectInfoExpanded ? 'active' : ''}`} />
          <div
            className="menu-item"
            onClick={handleProjectInfoClick}
            style={{ cursor: 'pointer' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
                stroke={isProjectInfoExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 6h4M8 10h4M8 14h2"
                stroke={isProjectInfoExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="menu-text">Project Info</span>
            <svg
              className={`dropdown-icon ${isProjectInfoExpanded ? 'rotate-180' : ''}`}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke={isProjectInfoExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        {isProjectInfoExpanded && (
          <div className="project-info-content">
            <ProjectInfo />
          </div>
        )}
      </>
    );
  };

  const DocManagementSection = () => {
    return (
      <>
        <div className={`menu-item-wrapper ${isDocManagementExpanded ? 'active' : ''}`}>
          <div className={`indicator ${isDocManagementExpanded ? 'active' : ''}`} />
          <div
            className="menu-item"
            onClick={() => setIsDocManagementExpanded(!isDocManagementExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 6C2 4.89543 2.89543 4 4 4H7L9 6H16C17.1046 6 18 6.89543 18 8V14C18 15.1046 17.1046 16 16 16H4C2.89543 16 2 15.1046 2 14V6Z"
                stroke={isDocManagementExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="menu-text">Doc Management</span>
            <svg
              className={`dropdown-icon ${isDocManagementExpanded ? 'rotate-180' : ''}`}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke={isDocManagementExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        {isDocManagementExpanded && <DocManagementLayout projectData={projectData} />}
      </>
    );
  };

  const LinksSection = () => {
    const { openAuthUrl, isReady } = useAuthFetch();
    const navigate = useNavigate();
    const viewParams = new URLSearchParams(location.search);
    const isDailyReport = viewParams.get("view") === "daily-report";
    const isTemplateReport = viewParams.get("view") === "report";
    const activeTemplateId = isTemplateReport ? viewParams.get("template") : null;
    const [activeLink, setActiveLink] = useState(null);

    // Links that shouldn't show "Coming Soon"
    const readyLinks = ["Acumatica", "Salesforce", "Reports"];

    const companyId = process.env.REACT_APP_ACUMETICA_COMPANY_ID || 'Dev';

    const handleLinkClick = (link) => {
      if (link === "Acumatica" && projectData?.projectId) {
        window.open(`https://westproconstruction.acumatica.com/Main?CompanyID=${companyId}&ScreenId=PM301000&BaseType=P&ContractCD=${projectData.projectId}+++`, '_blank');
      } else if (link === "Salesforce") {
        if (!projectData?.salesforceProjectId) {
          console.warn('Salesforce link unavailable: missing salesforceProjectId on selected project');
          return;
        }
        if (!isReady) {
          console.warn('Salesforce link unavailable: authentication not ready');
          return;
        }
        openAuthUrl(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/redirect/Project/${projectData.salesforceProjectId}`);
      } else if (link === "Reports") {
        setIsReportPickerOpen(true);
      }
    };

    return (
      <>
        <div className={`menu-item-wrapper ${isLinksExpanded ? 'active' : ''}`}>
          <div className={`indicator ${isLinksExpanded ? 'active' : ''}`} />
          <div
            className="menu-item"
            onClick={() => setIsLinksExpanded(!isLinksExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                stroke={isLinksExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
              />
              <path d="M2 10H18M10 2C12.5013 4.73835 13.9228 7.29203 14 10C13.9228 12.708 12.5013 15.2616 10 18C7.49872 15.2616 6.07725 12.708 6 10C6.07725 7.29203 7.49872 4.73835 10 2Z"
                stroke={isLinksExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
              />
            </svg>
            <span className="menu-text">Nettles Links</span>
            <svg
              className={`dropdown-icon ${isLinksExpanded ? 'rotate-180' : ''}`}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke={isLinksExpanded ? "#DC2626" : "#374151"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        {isLinksExpanded && (
          <div className="contacts-content">
            {links.map((link, index) => (
              <div
                key={index}
                className={`contact-tab-item ${activeLink === index || (link === "Reports" && (isDailyReport || isTemplateReport)) ? 'active' : ''}`}
                onClick={() => {
                  setActiveLink(index);
                  handleLinkClick(link);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="indicator"></div>
                <div className="tab-content flex justify-between items-center w-full">
                  <span>{link}</span>
                  {!readyLinks.includes(link) && (
                    <span className="text-xs text-red-600 font-medium">Coming Soon</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  if (isCollapsed) {
    return null;
  }

  return (
    <>
      <ReportPickerModal
        open={isReportPickerOpen}
        projectName={projectData?.projectName}
        onClose={() => setIsReportPickerOpen(false)}
        onConfirm={openSelectedReport}
      />
      {/* Mobile overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-overlay active"
          onClick={onMobileMenuClose}
          role="presentation"
          aria-hidden="true"
        />
      )}

      <aside
        className={`dashboard-sidebar${isMobile && isMobileMenuOpen ? ' mobile-open' : ''}${
          isStandalonePwa && isMobile ? ' dashboard-sidebar--pwa' : ''
        }`}
      >
        {isMobile && isMobileMenuOpen && (
          isStandalonePwa ? (
            <div className="sidebar-pwa-header">
              <div className="sidebar-pwa-logo">
                <img src={LogoImage} alt="Nettles Construction Solutions" className="sidebar-pwa-logo-image" />
              </div>
              <button
                type="button"
                className="sidebar-pwa-close"
                onClick={(event) => {
                  event.stopPropagation();
                  handleMobileMenuToggle();
                }}
                aria-label="Close menu"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M4 4L12 12M12 4L4 12"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="sidebar-mobile-toggle-row">
              <button
                type="button"
                className="sidebar-mobile-toggle"
                onClick={handleMobileMenuToggle}
                aria-label="Close menu"
              >
                <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M1 9H12M1 3H17M1 15H6" stroke="#AD2226" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )
        )}
        <nav className="sidebar-nav">
          <div className="flex flex-col h-full py-4">
            <div className="sidebar-content">
              {showGlobalMobileNav && (
                <>
                  <p className="sidebar-mobile-section-label">
                    {isStandalonePwa ? 'Applications' : 'App'}
                  </p>
                  {globalNavItems.map(renderNavLink)}
                  {isStandalonePwa ? (
                    <p className="sidebar-mobile-section-label">Quick access</p>
                  ) : null}
                  <RecentDashboardsSidebarSection onItemSelect={handleMenuClick} />
                </>
              )}

              {showGlobalMobileNav && showProjectSidebar && (
                <div className="sidebar-section-divider" aria-hidden="true" />
              )}

              {showProjectSidebar && (
                <>
                  {isMobile && !isGlobalNavPage && (
                    <p className="sidebar-mobile-section-label">This project</p>
                  )}
                  {dashboardItem && renderNavLink(dashboardItem)}
                  <ProjectInfoSection />
                  <DocManagementSection />
                  <ExternalLinksSection projectId={projectData?.projectId} />
                  <LinksSection />
                  {showPinnedProjectName && projectData?.projectName ? (
                    <div className="sidebar-project-slot">
                      <SidebarProjectPin key={projectData.projectId || projectData.projectName} projectData={projectData} />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
